import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { UserPlus, KeyRound, Power } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/utils/format";

const EMPTY = { employee_id: "", name: "", password: "", role: "employee", email: "", phone: "" };

export default function AdminEmployees() {
  const [emps, setEmps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [reset, setReset] = useState({ open: false, id: null, name: "", pw: "" });

  const load = () => api.get("/employees").then((r) => setEmps(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.employee_id || !form.name || !form.password) return toast.error("All fields required");
    try {
      await api.post("/employees", form);
      toast.success("Employee added");
      setForm(EMPTY);
      setOpen(false);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const toggle = async (emp) => {
    const next = emp.status === "active" ? "inactive" : "active";
    try {
      await api.patch(`/employees/${emp.id}`, { status: next });
      toast.success(`Employee ${next}`);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const doReset = async () => {
    if (reset.pw.length < 4) return toast.error("Password too short");
    try {
      await api.post(`/employees/${reset.id}/reset-password`, { new_password: reset.pw });
      toast.success("Password reset");
      setReset({ open: false, id: null, name: "", pw: "" });
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <AdminLayout>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-display font-bold">Employees</h1>
          <p className="text-sm text-slate-500 mt-1">Add, deactivate, and reset employee passwords.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-employee-btn" className="rounded-xl bg-[color:var(--bpcl-blue)] hover:bg-[color:var(--bpcl-blue-dark)]">
              <UserPlus className="w-4 h-4 mr-2" /> Add Employee
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New employee</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Employee ID" value={form.employee_id} onChange={(v) => setForm({ ...form, employee_id: v })} testId="new-emp-id" />
              <Field label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="new-emp-name" />
              <Field label="Password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} type="password" testId="new-emp-pw" />
              <div>
                <Label className="text-xs">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="new-emp-role" className="rounded-xl mt-1 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Field label="Email (optional)" value={form.email || ""} onChange={(v) => setForm({ ...form, email: v })} />
              <Field label="Phone (optional)" value={form.phone || ""} onChange={(v) => setForm({ ...form, phone: v })} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl">Cancel</Button>
              <Button onClick={create} data-testid="save-employee-btn" className="rounded-xl bg-[color:var(--bpcl-blue)] hover:bg-[color:var(--bpcl-blue-dark)]">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="rounded-2xl border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left text-xs uppercase text-slate-500">
              <th className="py-3 px-4">Employee</th>
              <th className="py-3 px-4">Role</th>
              <th className="py-3 px-4">Contact</th>
              <th className="py-3 px-4">Created</th>
              <th className="py-3 px-4">Status</th>
              <th className="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {emps.map((e) => (
              <tr key={e.id} data-testid={`emp-row-${e.employee_id}`} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="py-3 px-4">
                  <div className="font-medium">{e.name}</div>
                  <div className="text-xs text-slate-500 font-mono-num">{e.employee_id}</div>
                </td>
                <td className="py-3 px-4">
                  <Badge className={`rounded-full ${e.role === "admin" ? "bg-[color:var(--bpcl-blue)] text-white" : e.role === "manager" ? "bg-[color:var(--bpcl-orange)] text-white" : "bg-slate-100 text-slate-700"} border-transparent`}>
                    {e.role}
                  </Badge>
                </td>
                <td className="py-3 px-4 text-slate-600">
                  <div className="text-xs">{e.email || "—"}</div>
                  <div className="text-xs">{e.phone || "—"}</div>
                </td>
                <td className="py-3 px-4 text-slate-600 text-xs">{fmtDate(e.created_at)}</td>
                <td className="py-3 px-4">
                  <Badge variant={e.status === "active" ? "default" : "secondary"} className="rounded-full">
                    {e.status}
                  </Badge>
                </td>
                <td className="py-3 px-4 text-right space-x-2">
                  <Button
                    size="sm" variant="outline" className="rounded-full"
                    onClick={() => setReset({ open: true, id: e.id, name: e.name, pw: "" })}
                    data-testid={`reset-pw-${e.employee_id}`}
                  >
                    <KeyRound className="w-3.5 h-3.5 mr-1" /> Reset
                  </Button>
                  <Button
                    size="sm" variant="outline" className="rounded-full"
                    onClick={() => toggle(e)}
                    data-testid={`toggle-${e.employee_id}`}
                  >
                    <Power className="w-3.5 h-3.5 mr-1" /> {e.status === "active" ? "Deactivate" : "Activate"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={reset.open} onOpenChange={(v) => setReset((r) => ({ ...r, open: v }))}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset password — {reset.name}</DialogTitle></DialogHeader>
          <div className="space-y-1.5">
            <Label>New password</Label>
            <Input
              type="password"
              value={reset.pw}
              onChange={(e) => setReset((r) => ({ ...r, pw: e.target.value }))}
              data-testid="reset-pw-input"
              className="h-11 rounded-xl"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReset({ open: false, id: null, name: "", pw: "" })} className="rounded-xl">Cancel</Button>
            <Button onClick={doReset} data-testid="reset-pw-submit" className="rounded-xl bg-[color:var(--bpcl-blue)] hover:bg-[color:var(--bpcl-blue-dark)]">Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="h-11 rounded-xl mt-1" />
    </div>
  );
}
